import { Type } from '@angular/core';

import moment from 'moment-timezone';

import { FilterTypesEnum } from '../../../../../Common/Filter/enums/filters-types.enum';
import { AbstractFilterTypeComponent } from '../../../../../Common/Filter/models/abstract-filter-type.component';
import { CustomFilterTypeModel } from '../../../../../Common/Filter/models/custom-filter-type.model';
import { ChipModel } from '../../../../../Common/Forms/models/chip.model';
import { EntityType } from '@api/targeting/enum/entity.type.enum';

export enum StatusFilterType {
    systemContacts = 'Discover.targeting.filter.contactType.system',
    privateContacts = 'Discover.targeting.filter.contactType.private',
    longstanding = 'Discover.statusFilter.longstanding',
    multipleEmployments = 'Discover.statusFilter.multipleEmployments',
    created = 'Discover.statusFilter.newSinceChipLabel',
    updated = 'Discover.statusFilter.updatedSinceChipLabel',
    /** Recently Verified filter hidden for now (10-1) */
    // verified = 'Discover.statusFilter.verifiedSinceLabel',
    newEmployment = 'Discover.statusFilter.newEmploymentSinceChipLabel',
    activeNotices = 'Discover.statusFilter.activeNoticesLabel',
    aiReferenceLeader = 'Discover.statusFilter.aiReferenceLeader'
}

class StatusFilterChip implements ChipModel {
    label: string;
    type: StatusFilterType;
    labelArgs: {};

    constructor(type: StatusFilterType, label: string, labelArgs?: {}) {
        this.type = type;
        this.label = label;
        this.labelArgs = labelArgs;
    }
}

export class StatusFilterData {
    includeSystemContacts: boolean;
    includePrivateContacts: boolean;
    isLongstanding: boolean;
    hasMultipleEmployments: boolean;
    hasActiveNotices: boolean;
    isAiReference: boolean;

    createdDate: Date;
    updatedDate: Date;
    // verifiedDate: Date;
    newEmploymentDate: Date;

    constructor() {
        this.includeSystemContacts = true;
        this.includePrivateContacts = true;
    }

    clone(): StatusFilterData {
        const clone = new StatusFilterData();
        clone.includeSystemContacts = this.includeSystemContacts;
        clone.includePrivateContacts = this.includePrivateContacts;
        clone.createdDate = this.createdDate ? new Date(this.createdDate.getTime()) : undefined;
        clone.updatedDate = this.updatedDate ? new Date(this.updatedDate.getTime()) : undefined;
        // clone.verifiedDate = this.verifiedDate ? new Date(this.verifiedDate.getTime()) : undefined;
        clone.newEmploymentDate = this.newEmploymentDate ? new Date(this.newEmploymentDate.getTime()) : undefined;
        clone.isLongstanding = this.isLongstanding;
        clone.hasMultipleEmployments = this.hasMultipleEmployments;
        clone.hasActiveNotices = this.hasActiveNotices;
        clone.isAiReference = this.isAiReference;
        return clone;
    }
}

export class StatusFilterModel extends CustomFilterTypeModel<StatusFilterData> {
    readonly key: FilterTypesEnum = FilterTypesEnum.custom;

    protected value: StatusFilterData;
    protected currentValue: StatusFilterData;

    chips: ChipModel[] = [];
    currentChips: ChipModel[] = [];
    entityType: EntityType;

    constructor(
        id: string,
        label: string,
        component: Type<AbstractFilterTypeComponent>,
        section?: string
    ) {
        super(id, label, component, section);
        this.setValue(new StatusFilterData());
    }

    setValue(value: StatusFilterData): void {
        this.value = value;
        this.setCurrentValue(this.value.clone());
        if (this.value) {
            this.chips = [];
            this.updateContactTypeChip(this.chips, this.value);
            this.updateDateChip(this.chips, this.value.createdDate, StatusFilterType.created);
            this.updateDateChip(this.chips, this.value.updatedDate, StatusFilterType.updated);
            // this.updateDateChip(this.chips, this.value.verifiedDate, StatusFilterType.verified);
            this.updateDateChip(this.chips, this.value.newEmploymentDate, StatusFilterType.newEmployment);
            this.updateBooleanChip(this.chips, this.value.isLongstanding, StatusFilterType.longstanding);
            this.updateBooleanChip(this.chips, this.value.hasMultipleEmployments, StatusFilterType.multipleEmployments);
            this.updateBooleanChip(this.chips, this.value.hasActiveNotices, StatusFilterType.activeNotices);
            this.updateBooleanChip(this.chips, this.value.isAiReference, StatusFilterType.aiReferenceLeader);
        }
    }

    getValue(): StatusFilterData {
        return this.value;
    }

    // Chip removed from the chip container outside of the filter popup (permanent)
    removeChip(chip: StatusFilterChip): void {
        switch (chip.type) {
            case StatusFilterType.systemContacts:
            case StatusFilterType.privateContacts:
                this.value.includeSystemContacts = this.value.includePrivateContacts = true;
                break;
            case StatusFilterType.created:
                this.value.createdDate = undefined;
                break;
            case StatusFilterType.updated:
                this.value.updatedDate = undefined;
                break;
            // case StatusFilterType.verified:
            //     this.value.verifiedDate = undefined;
            //     break;
            case StatusFilterType.newEmployment:
                this.value.newEmploymentDate = undefined;
                break;
            case StatusFilterType.longstanding:
                this.value.isLongstanding = false;
                break;
            case StatusFilterType.multipleEmployments:
                this.value.hasMultipleEmployments = false;
                break;
            case StatusFilterType.activeNotices:
                this.value.hasActiveNotices = false;
                break;
            case StatusFilterType.aiReferenceLeader:
                this.value.isAiReference = false;
                break;
        }
        this.setValue(this.value);
    }

    // Chip removed from the right panel of the filter popup (temporary)
    removeCurrentChip(chip: StatusFilterChip): void {
        switch (chip.type) {
            case StatusFilterType.systemContacts:
            case StatusFilterType.privateContacts:
                this.currentValue.includeSystemContacts = this.currentValue.includePrivateContacts = true;
                break;
            case StatusFilterType.longstanding:
                this.currentValue.isLongstanding = false;
                break;
            case StatusFilterType.multipleEmployments:
                this.currentValue.hasMultipleEmployments = false;
                break;
            case StatusFilterType.created:
                this.currentValue.createdDate = undefined;
                break;
            case StatusFilterType.updated:
                this.currentValue.updatedDate = undefined;
                break;
            // case StatusFilterType.verified:
            //     this.currentValue.verifiedDate = undefined;
            //     break;
            case StatusFilterType.newEmployment:
                this.currentValue.newEmploymentDate = undefined;
                break;
            case StatusFilterType.activeNotices:
                this.currentValue.hasActiveNotices = false;
                break;
            case StatusFilterType.aiReferenceLeader:
                this.value.isAiReference = false;
                break;
        }
        this.setCurrentValue(this.currentValue);
    }

    clear(): void {
        this.setValue(new StatusFilterData());
    }

    cancelChanges(): void {
        this.setCurrentValue(this.value.clone());
    }

    getCurrentValue(): StatusFilterData {
        return this.currentValue;
    }

    setCurrentValue(value: StatusFilterData): void {
        this.currentValue = value;
        if (this.currentValue) {
            this.currentChips = [];
            this.updateContactTypeChip(this.currentChips, this.currentValue);
            this.updateDateChip(this.currentChips, this.currentValue.createdDate, StatusFilterType.created);
            this.updateDateChip(this.currentChips, this.currentValue.updatedDate, StatusFilterType.updated);
            // this.updateDateChip(this.currentChips, this.currentValue.verifiedDate, StatusFilterType.verified);
            this.updateDateChip(this.currentChips, this.currentValue.newEmploymentDate, StatusFilterType.newEmployment);
            this.updateBooleanChip(this.currentChips, this.currentValue.isLongstanding, StatusFilterType.longstanding);
            this.updateBooleanChip(this.currentChips, this.currentValue.hasMultipleEmployments, StatusFilterType.multipleEmployments);
            this.updateBooleanChip(this.currentChips, this.currentValue.hasActiveNotices, StatusFilterType.activeNotices);
            this.updateBooleanChip(this.currentChips, this.currentValue.isAiReference, StatusFilterType.aiReferenceLeader);
        }
    }

    hasFiltersToApply(): boolean {
        return (
            this.value.includeSystemContacts !== this.value.includePrivateContacts
            || !!this.value.createdDate
            || !!this.value.updatedDate
            // || !!this.value.verifiedDate
            || !!this.value.newEmploymentDate
            || this.value.isLongstanding
            || this.value.hasMultipleEmployments
            || this.value.hasActiveNotices
            || this.value.isAiReference
        );
    }

    updateContactTypeChip(chips: ChipModel[], contactType: StatusFilterData) {
        if (!chips) {
            return;
        }

        if (contactType.includeSystemContacts && !contactType.includePrivateContacts) {
            chips.push(new StatusFilterChip(StatusFilterType.systemContacts, StatusFilterType.systemContacts));
        } else if (!contactType.includeSystemContacts && contactType.includePrivateContacts) {
            chips.push(new StatusFilterChip(StatusFilterType.privateContacts, StatusFilterType.privateContacts));
        }
    }

    updateDateChip(chips: ChipModel[], date: Date, chipType: StatusFilterType) {
        if (!chips) {
            return;
        }

        if (date) {
            chips.push(
                new StatusFilterChip(chipType, chipType, { date: moment(date).format('MMMM DD, YYYY')})
            );
        }
    }

    updateBooleanChip(chips: ChipModel[], flag: boolean, chipType: StatusFilterType) {
        if (!chips) {
            return;
        }

        if (flag) {
            chips.push(new StatusFilterChip(chipType, chipType));
        }
    }
}
